import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Box, DataTableColumns, HStack, Button, useBanner } from '@tq/ui-components';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import { IncomingConsultation, IncomingConsultationRequestStatus, ReportData } from 'types';
import { postRetryIncomingConsultation } from 'api';
import { useEffect, useState } from 'react';
import { useFormatLocaleDate, StandardDateFormats } from 'config';

export const useIncomingConsultationsTrafficMonitorColumns = (
  isRefreshing: boolean
): DataTableColumns<ReportData>[] => {
  const [disableButton, setDisabledButton] = useState(false);
  const { t } = useTranslation('traffic_monitor');
  const banner = useBanner();
  const formatLocaleDate = useFormatLocaleDate();

  useEffect(() => {
    if (isRefreshing && disableButton) {
      setDisabledButton(!disableButton);
    }
  }, [disableButton, isRefreshing]);

  const handleRetry = async (incomingConsultationId: number) => {
    setDisabledButton(!disableButton);
    const response = await postRetryIncomingConsultation({
      id: incomingConsultationId,
      topicName: 'IncomingConsultationReceived',
    });
    banner({
      id: 'retry-banner',
      title: response.body,
    });
  };

  return [
    {
      accessor: 'id',
      Cell: (cellProps: { value: string }) => cellProps.value,
      Header: () => t('request_id', 'Request ID'),
      width: 50,
    },
    {
      accessor: 'messageId',
      Cell: (cellProps: { value: string }) => cellProps.value,
      Header: () => t('message_id', 'Message ID'),
      disableSortBy: true,
      width: 260,
    },
    {
      accessor: 'newOrUpdate',
      Cell: (cellProps: { value: string }) => (cellProps.value === 'N' ? 'New' : 'Update'),
      Header: () => t('new_or_update', 'New or Update'),
      disableSortBy: true,
      width: 50,
    },
    {
      accessor: 'documentCount',
      Cell: (cellProps: { value: number }) =>
        cellProps.value > 0
          ? cellProps.value === 1
            ? '1 Document'
            : cellProps.value + ' Documents'
          : 'None',
      Header: () => t('documents_attached', 'Documents attached'),
      disableSortBy: true,
      width: 300,
    },
    {
      accessor: 'incomingStatus',
      Cell: (cellProps: { value: IncomingConsultationRequestStatus }) => (
        <HStack>
          <Box
            color={cellProps.value === 'Processed' ? COLOURS.green[400] : COLOURS.red[400]}
            marginRight={2}
            fontSize={20}
            lineHeight={24}
          >
            {cellProps.value === 'Processed' ? <FaCheckCircle /> : <FaTimesCircle />}
          </Box>
          <Box>{cellProps.value}</Box>
        </HStack>
      ),
      Header: () => t('status_header', 'Status'),
      disableSortBy: true,
      width: 100,
    },
    {
      accessor: 'dateCreated',
      Cell: (cellProps: { value: string }) =>
        formatLocaleDate(cellProps.value, StandardDateFormats.dateAndTime).toLowerCase(),
      Header: () => t('date_request_created', 'Date request created'),
      width: 420,
    },
    {
      accessor: 'retry',
      Cell: ({ row: { original } }: CellProps<IncomingConsultation>) => (
        <Button
          ml='auto'
          size='xs'
          variant='secondary'
          onClick={() => handleRetry(original.id)}
          disabled={disableButton}
        >
          {t('retry_label', 'Retry')}
        </Button>
      ),
      width: 50,
      disableSortBy: true,
    },
  ];
};
